import { useEffect } from 'react';

import { selectIsAccountEligible } from 'modules/referralProgram/store/selectors';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAppSelector } from 'store/useAppSelector';
import { useApplyReferralCode } from 'modules/referralProgram/hooks/useApplyReferralCode';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { usePersonalAccountStatus } from 'modules/personalAccount/hooks/usePersonalAccountStatus';
import { useReferrer } from 'modules/referralProgram/hooks/useReferrer';
import { useSavedReferralCode } from 'modules/referralProgram/hooks/useSavedReferralCode';
import { useUser } from 'modules/user/hooks/useUser';
import { useValidateReferralCode } from 'modules/referralProgram/hooks/useValidateReferralCode';

export interface IUseInitReferralFlowWithSavedCodeProps {
  handleIneligibleAccountDialogOpen: () => void;
  handleSuccessDialogOpen: () => void;
  handleSwitchAccountDialogOpen: () => void;
  isBannerLoaded: boolean;
}

export const useInitReferralFlowWithSavedCode = ({
  handleIneligibleAccountDialogOpen,
  handleSuccessDialogOpen,
  handleSwitchAccountDialogOpen,
  isBannerLoaded,
}: IUseInitReferralFlowWithSavedCodeProps) => {
  const { isLoggedIn } = useAuth();
  const { isUserInitialized } = useUser();

  const { isPersonalAccount } = useAccount();

  const {
    isPersonalAccountFreemium,
    isPersonalAccountStatusLoaded,
    userEndpointToken,
  } = usePersonalAccountStatus({ skipFetching: !isUserInitialized });

  const isAccountEligible = useAppSelector(state =>
    selectIsAccountEligible(state, { userEndpointToken: userEndpointToken! }),
  );

  const { handleRemoveSavedReferralCode, savedReferralCode } =
    useSavedReferralCode();

  const { handleApplyReferralCode, isApplied, isApplying } =
    useApplyReferralCode({
      hasSuccessNotification: false,
      onError: handleRemoveSavedReferralCode,
      onSuccess: handleSuccessDialogOpen,
      referralCode: savedReferralCode,
      // needs to display Success dialog correctly
      shouldRemoveSavedData: false,
    });

  const { validateReferralCode } = useValidateReferralCode();

  const { isLoading: isReferrerLoading, referrer } = useReferrer({
    skipFetching: true,
  });

  const isReferrerLoaded = typeof referrer !== 'undefined';

  const hasReferrer = Boolean(referrer);

  useEffect(() => {
    const error = validateReferralCode(savedReferralCode);

    const shouldContinueReferralFlow =
      !error &&
      isLoggedIn &&
      isBannerLoaded &&
      isPersonalAccountStatusLoaded &&
      !hasReferrer &&
      isReferrerLoaded &&
      !isApplying &&
      !isReferrerLoading &&
      !isApplied;

    if (shouldContinueReferralFlow) {
      if (isAccountEligible) {
        handleApplyReferralCode();
      } else if (!isPersonalAccount && isPersonalAccountFreemium) {
        handleSwitchAccountDialogOpen();
      } else {
        handleIneligibleAccountDialogOpen();
      }
    }
    // Only data should be tracked, not callbacks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasReferrer,
    isAccountEligible,
    isApplied,
    isApplying,
    isBannerLoaded,
    isLoggedIn,
    isPersonalAccount,
    isPersonalAccountFreemium,
    isPersonalAccountStatusLoaded,
    isReferrerLoaded,
    isReferrerLoading,
    savedReferralCode,
  ]);
};
